package com.gavino.nutritrack

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import kotlin.math.abs
import kotlin.math.roundToInt
import kotlin.random.Random

private val EmeraldPrimary = Color(0xFF059669)
private val EmeraldDark = Color(0xFF047857)
private val EmeraldLight = Color(0xFFECFDF5)
private val AmberColor = Color(0xFFD97706)
private val AmberLight = Color(0xFFFFFBEB)
private val BlueColor = Color(0xFF2563EB)
private val BlueLight = Color(0xFFEFF6FF)
private val RoseColor = Color(0xFFE11D48)
private val RoseLight = Color(0xFFFFF1F2)
private val PurpleColor = Color(0xFF7C3AED)
private val PurpleLight = Color(0xFFF5F3FF)
private val SlateBg = Color(0xFFF8FAFC)
private val SlateCardBorder = Color(0xFFE2E8F0)
private val SlateTextMuted = Color(0xFF64748B)
private val SlateTextDark = Color(0xFF0F172A)

enum class AppMode {
    DIETOLOGIST_ORIGINAL,
    SMART_AI_PROPOSALS,
    WEIGHT_DIARY,
    USER_PROFILE_TDEE
}

@Composable
fun App() {
    MaterialTheme(
        colorScheme = lightColorScheme(
            primary = EmeraldPrimary,
            secondary = AmberColor,
            background = SlateBg,
            surface = Color.White
        )
    ) {
        NutriTrackMainScreen()
    }
}

@Composable
fun NutriTrackMainScreen() {
    var activeMode by remember { mutableStateOf(AppMode.DIETOLOGIST_ORIGINAL) }
    val coroutineScope = rememberCoroutineScope()

    var userProfile by remember { mutableStateOf(UserProfileData()) }
    val metabolicReport = remember(userProfile) {
        UserProfileEngine.calculateMetrics(userProfile)
    }

    var weightLog by remember {
        mutableStateOf(
            listOf(
                WeightEntry("w1", "Inizio Percorso", 84.0, 92, "Prima pesata di riferimento")
            )
        )
    }

    var selectedDayNum by remember { mutableStateOf(1) }
    var currentPlanDay by remember { mutableStateOf(DietRepository.planDays.first()) }
    var mealsMap by remember { mutableStateOf(currentPlanDay.meals) }
    var allDaysMeals by remember {
        mutableStateOf(DietRepository.planDays.associate { it.dayNum to it.meals })
    }
    var smartLowCarbLunch by remember { mutableStateOf(false) }

    // Calcolo settimana ancorato al Lunedì (Lunedì..Domenica)
    val todayDateStr = getTodayDateLabel()
    val yesterdayDateStr = getRelativeDateLabel(-1)
    val (mondayWeekKey, validWeekDates) = remember(todayDateStr) {
        WeeklyCalendarHelper.getCurrentWeekMondayKeyAndDates(todayDateStr)
    }

    var assignedDaysMap by remember { mutableStateOf<Map<Int, String>>(emptyMap()) }
    var currentWeekKey by remember { mutableStateOf(mondayWeekKey) }

    // Target calorico giornaliero bloccato (se > 0, tutti i giorni G1..G7 restano fissi su questo tetto Kcal!)
    var lockedTargetKcal by remember { mutableStateOf<Int?>(null) }
    var targetCalInput by remember { mutableStateOf(currentPlanDay.baseTotalCal.toString()) }
    var scaleFactor by remember { mutableStateOf(1.0) }
    var waterGlasses by remember { mutableStateOf(0) }
    var targetWaterGlasses by remember { mutableStateOf(8) }

    var activeSwapRequest by remember { mutableStateOf<Triple<MealSectionType, Int, FoodItem>?>(null) }
    var activeConsumeRequest by remember { mutableStateOf<Triple<MealSectionType, Int, FoodItem>?>(null) }
    var activeAddExtraSection by remember { mutableStateOf<MealSectionType?>(null) }
    var activeRecipeModalId by remember { mutableStateOf<String?>(null) }
    var showAllRecipesModal by remember { mutableStateOf(false) }
    var showCloudSyncModal by remember { mutableStateOf(false) }

    var activeShareContent by remember { mutableStateOf<Pair<String, String>?>(null) }

    var syncStatusBanner by remember { mutableStateOf("☁️ Connessione a Firebase nutritrack-36ff6...") }
    var isInitialLoadDone by remember { mutableStateOf(false) }

    /**
     * Applica il target calorico scelto (es. 1300 kcal) a TUTTI i 7 giorni della dieta,
     * garantendo che nessun giorno superi mai il target impostato!
     */
    fun applyCalorieRescaleToAllDays(newTargetCal: Int) {
        if (newTargetCal < 600) return
        lockedTargetKcal = newTargetCal
        targetCalInput = newTargetCal.toString()

        val updatedAll = allDaysMeals.toMutableMap()
        DietRepository.planDays.forEach { planDay ->
            val currentDayMeals = updatedAll[planDay.dayNum] ?: planDay.meals
            updatedAll[planDay.dayNum] = FoodItem.scaleDayMealsToExactTarget(
                meals = currentDayMeals,
                dayBaseTotalCal = planDay.baseTotalCal,
                targetKcal = newTargetCal
            )
        }
        allDaysMeals = updatedAll

        val factor = if (currentPlanDay.baseTotalCal > 0) {
            newTargetCal.toDouble() / currentPlanDay.baseTotalCal.toDouble()
        } else 1.0
        scaleFactor = factor
        mealsMap = updatedAll[selectedDayNum] ?: currentPlanDay.meals
    }

    LaunchedEffect(Unit) {
        val remoteState = FirebaseSyncManager.pullFromFirebase()
        if (remoteState != null) {
            selectedDayNum = remoteState.selectedDayNum
            val foundDay = DietRepository.planDays.find { it.dayNum == remoteState.selectedDayNum }
                ?: DietRepository.planDays.first()
            currentPlanDay = foundDay
            waterGlasses = remoteState.waterGlasses
            userProfile = remoteState.userProfile
            weightLog = remoteState.weightLog

            // Mantieni tutte le date assegnate che appartengono alla settimana Lunedì-Domenica corrente!
            val validAssigned = remoteState.assignedDaysMap.filterValues { dateVal ->
                dateVal in validWeekDates
            }
            assignedDaysMap = validAssigned
            currentWeekKey = mondayWeekKey

            val savedTarget = remoteState.targetCalInput.toIntOrNull()
            val mergedAllDays = DietRepository.planDays.associate { d ->
                d.dayNum to (remoteState.allDaysMeals[d.dayNum] ?: d.meals)
            }.toMutableMap()

            // Se l'utente aveva impostato un target calorico (es. 1300 kcal), mantienilo bloccato su tutti i 7 giorni
            if (savedTarget != null && savedTarget >= 600 && abs(remoteState.scaleFactor - 1.0) >= 0.01) {
                lockedTargetKcal = savedTarget
                targetCalInput = savedTarget.toString()
                DietRepository.planDays.forEach { d ->
                    val m = mergedAllDays[d.dayNum] ?: d.meals
                    mergedAllDays[d.dayNum] = FoodItem.scaleDayMealsToExactTarget(m, d.baseTotalCal, savedTarget)
                }
                scaleFactor = savedTarget.toDouble() / foundDay.baseTotalCal.toDouble()
            } else {
                lockedTargetKcal = null
                scaleFactor = 1.0
                targetCalInput = foundDay.baseTotalCal.toString()
            }

            allDaysMeals = mergedAllDays
            mealsMap = mergedAllDays[selectedDayNum] ?: foundDay.meals
            syncStatusBanner = "☁️ Sincronizzato con Firebase (nutritrack-36ff6) ✓"
        } else {
            syncStatusBanner = "☁️ Pronto • Sincronizzazione Firebase attiva"
        }
        isInitialLoadDone = true
    }

    // Reset automatico SOLO quando scatta il nuovo Lunedì
    LaunchedEffect(mondayWeekKey) {
        if (isInitialLoadDone && mondayWeekKey != currentWeekKey) {
            currentWeekKey = mondayWeekKey
            assignedDaysMap = emptyMap()
        }
    }

    LaunchedEffect(selectedDayNum, scaleFactor, targetCalInput, waterGlasses, userProfile, weightLog, mealsMap, assignedDaysMap) {
        if (!isInitialLoadDone) return@LaunchedEffect
        val updatedAllDays = allDaysMeals.toMutableMap().apply { put(selectedDayNum, mealsMap) }
        allDaysMeals = updatedAllDays
        val snapshot = CloudAppState(
            selectedDayNum = selectedDayNum,
            scaleFactor = scaleFactor,
            targetCalInput = targetCalInput,
            waterGlasses = waterGlasses,
            userProfile = userProfile,
            weightLog = weightLog,
            allDaysMeals = updatedAllDays,
            assignedDaysMap = assignedDaysMap,
            weekKey = currentWeekKey
        )
        val ok = FirebaseSyncManager.pushToFirebase(snapshot)
        syncStatusBanner = if (ok) {
            "☁️ Salvato in tempo reale su Firebase ✓"
        } else {
            "☁️ Dati aggiornati in locale (Usa Sync per forzare Cloud)"
        }
    }

    fun markCurrentDayAsAssigned(dateLabel: String = getTodayDateLabel()) {
        if (assignedDaysMap[selectedDayNum] != dateLabel) {
            assignedDaysMap = assignedDaysMap.toMutableMap().apply { put(selectedDayNum, dateLabel) }
        }
    }

    fun selectOriginalDay(dayNum: Int) {
        activeMode = AppMode.DIETOLOGIST_ORIGINAL
        val updatedAll = allDaysMeals.toMutableMap().apply { put(selectedDayNum, mealsMap) }

        selectedDayNum = dayNum
        val found = DietRepository.planDays.find { it.dayNum == dayNum } ?: DietRepository.planDays.first()
        currentPlanDay = found

        val rawDayMeals = updatedAll[dayNum] ?: found.meals
        val activeTarget = lockedTargetKcal

        if (activeTarget != null && activeTarget >= 600) {
            // Mantieni il target calorico fisso (es. 1300 kcal) su TUTTI i giorni G1-G7!
            targetCalInput = activeTarget.toString()
            scaleFactor = activeTarget.toDouble() / found.baseTotalCal.toDouble()
            val scaledForDay = FoodItem.scaleDayMealsToExactTarget(rawDayMeals, found.baseTotalCal, activeTarget)
            updatedAll[dayNum] = scaledForDay
            allDaysMeals = updatedAll
            mealsMap = scaledForDay
        } else {
            scaleFactor = 1.0
            targetCalInput = found.baseTotalCal.toString()
            allDaysMeals = updatedAll
            mealsMap = rawDayMeals
        }
    }

    fun generateNewSmartDay(lowCarbLunch: Boolean = smartLowCarbLunch) {
        activeMode = AppMode.SMART_AI_PROPOSALS
        smartLowCarbLunch = lowCarbLunch
        val targetCal = lockedTargetKcal ?: targetCalInput.toIntOrNull() ?: 1500
        val generated = SmartChefEngine.generateFullSmartDay(
            targetCalories = targetCal,
            preferLowCarbLunch = lowCarbLunch,
            seed = Random.nextInt(100, 999)
        )
        currentPlanDay = generated
        scaleFactor = 1.0
        mealsMap = generated.meals
    }

    fun resetToOriginalWeights() {
        lockedTargetKcal = null
        scaleFactor = 1.0
        targetCalInput = currentPlanDay.baseTotalCal.toString()
        val resetAll = DietRepository.planDays.associate { d ->
            val currentMeals = allDaysMeals[d.dayNum] ?: d.meals
            d.dayNum to currentMeals.mapValues { (_, list) -> list.map { it.scaleByFactor(1.0) } }
        }
        allDaysMeals = resetAll
        mealsMap = resetAll[selectedDayNum] ?: currentPlanDay.meals
    }

    fun restoreSingleOriginalItem(section: MealSectionType, itemIdx: Int) {
        val originalDay = DietRepository.planDays.find { it.dayNum == selectedDayNum } ?: return
        val origItem = originalDay.meals[section]?.getOrNull(itemIdx) ?: return
        val scaledOrig = origItem.scaleByFactor(scaleFactor)
        val mutableList = (mealsMap[section] ?: emptyList()).toMutableList()
        if (itemIdx in mutableList.indices) {
            mutableList[itemIdx] = scaledOrig
            mealsMap = mealsMap.toMutableMap().apply { put(section, mutableList) }
        }
    }

    val allItems = mealsMap.values.flatten()
    val prescribedCal = allItems.filter { !it.isExtraItem }.sumOf { it.currentCal }
    val prescribedCarb = allItems.filter { !it.isExtraItem }.sumOf { it.currentCarb }.roundToInt().coerceAtLeast(1)
    val prescribedProt = allItems.filter { !it.isExtraItem }.sumOf { it.currentProt }.roundToInt().coerceAtLeast(1)
    val prescribedFat = allItems.filter { !it.isExtraItem }.sumOf { it.currentFat }.roundToInt().coerceAtLeast(1)

    val eatenCal = allItems.sumOf { it.eatenCal }
    val eatenCarb = (allItems.sumOf { it.eatenCarb } * 10.0).roundToInt() / 10.0
    val eatenProt = (allItems.sumOf { it.eatenProt } * 10.0).roundToInt() / 10.0
    val eatenFat = (allItems.sumOf { it.eatenFat } * 10.0).roundToInt() / 10.0
    val consumedCount = allItems.count { it.isConsumed }

    // --- INTEGRAZIONE HEALTH CONNECT (SMARTWATCH) ---
    val healthManager = remember { HealthConnectManager() }
    var activeCaloriesFromWatch by remember { mutableStateOf(0) }
    var healthDateOffset by remember { mutableStateOf(0) }

    fun refreshHealthData() {
        coroutineScope.launch {
            val hasPerms = healthManager.requestPermissions()
            if (hasPerms) {
                activeCaloriesFromWatch = healthManager.readActiveCaloriesForDay(healthDateOffset)
                if (healthDateOffset == 0 && eatenCal > 0) {
                    healthManager.writeNutritionToday(eatenCal.toDouble(), eatenCarb, eatenProt, eatenFat)
                }
            }
        }
    }

    LaunchedEffect(healthDateOffset) {
        if (healthManager.isAvailable()) {
            activeCaloriesFromWatch = healthManager.readActiveCaloriesForDay(healthDateOffset)
        }
    }

    val totalOutputCal = metabolicReport.bmrKcal + activeCaloriesFromWatch
    val realDeficit = totalOutputCal - eatenCal

    Scaffold(
        containerColor = SlateBg,
        topBar = {
            Surface(color = EmeraldPrimary, shadowElevation = 4.dp) {
                Column(modifier = Modifier.statusBarsPadding()) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                            Text(
                                text = "🔥 Piano Dietosystem",
                                color = Color.White,
                                fontSize = 16.sp,
                                fontWeight = FontWeight.ExtraBold,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = syncStatusBanner,
                                color = Color.White.copy(alpha = 0.92f),
                                fontSize = 10.sp,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                            Button(
                                onClick = { refreshHealthData() },
                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0284C7)),
                                shape = RoundedCornerShape(8.dp),
                                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                                modifier = Modifier.height(32.dp)
                            ) {
                                Text("⌚ Salute", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                            }
                            Button(
                                onClick = { showCloudSyncModal = true },
                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFBBF24)),
                                shape = RoundedCornerShape(8.dp),
                                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                                modifier = Modifier.height(32.dp)
                            ) {
                                Text("☁️ Sync", color = SlateTextDark, fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
                            }
                            Button(
                                onClick = {
                                    activeRecipeModalId = null
                                    showAllRecipesModal = true
                                },
                                colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.2f)),
                                shape = RoundedCornerShape(8.dp),
                                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                                modifier = Modifier.height(32.dp)
                            ) {
                                Text("📖 Ricette", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                            }
                        }
                    }

                    Surface(color = EmeraldDark) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 8.dp, vertical = 6.dp),
                            horizontalArrangement = Arrangement.spacedBy(5.dp)
                        ) {
                            NavTabButton(
                                label = "📋 Dieta",
                                isSelected = activeMode == AppMode.DIETOLOGIST_ORIGINAL,
                                activeBg = Color.White,
                                activeText = EmeraldDark,
                                modifier = Modifier.weight(1f),
                                onClick = { activeMode = AppMode.DIETOLOGIST_ORIGINAL }
                            )
                            NavTabButton(
                                label = "✨ Smart AI",
                                isSelected = activeMode == AppMode.SMART_AI_PROPOSALS,
                                activeBg = Color(0xFFFBBF24),
                                activeText = SlateTextDark,
                                modifier = Modifier.weight(1f),
                                onClick = {
                                    if (activeMode != AppMode.SMART_AI_PROPOSALS) generateNewSmartDay(smartLowCarbLunch)
                                }
                            )
                            NavTabButton(
                                label = "⚖️ Peso",
                                isSelected = activeMode == AppMode.WEIGHT_DIARY,
                                activeBg = Color.White,
                                activeText = PurpleColor,
                                modifier = Modifier.weight(1f),
                                onClick = { activeMode = AppMode.WEIGHT_DIARY }
                            )
                            NavTabButton(
                                label = "👤 TDEE",
                                isSelected = activeMode == AppMode.USER_PROFILE_TDEE,
                                activeBg = Color.White,
                                activeText = BlueColor,
                                modifier = Modifier.weight(1f),
                                onClick = { activeMode = AppMode.USER_PROFILE_TDEE }
                            )
                        }
                    }
                }
            }
        }
    ) { paddingValues: PaddingValues ->
        when (activeMode) {
            AppMode.USER_PROFILE_TDEE -> {
                UserProfileScreen(
                    profile = userProfile,
                    report = metabolicReport,
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    onUpdateProfile = { userProfile = it },
                    onApplyTargetToDiet = { newTargetKcal, newWaterGlasses ->
                        targetWaterGlasses = newWaterGlasses
                        activeMode = AppMode.DIETOLOGIST_ORIGINAL
                        applyCalorieRescaleToAllDays(newTargetKcal)
                    }
                )
            }

            AppMode.WEIGHT_DIARY -> {
                WeightDiaryScreen(
                    currentWeight = userProfile.currentWeightKg,
                    targetWeight = userProfile.targetWeightKg,
                    currentWaist = userProfile.waistCm,
                    entries = weightLog,
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    onAddWeightEntry = { newWeight, newWaist, dateStr, note ->
                        val entry = WeightEntry(
                            id = "w_${Random.nextInt(1000, 9999)}",
                            dateLabel = dateStr,
                            weightKg = newWeight,
                            waistCm = newWaist,
                            note = note
                        )
                        weightLog = listOf(entry) + weightLog
                        userProfile = userProfile.copy(currentWeightKg = newWeight, waistCm = newWaist)
                        coroutineScope.launch {
                            healthManager.writeWeight(newWeight)
                        }
                    },
                    onDeleteEntry = { id ->
                        weightLog = weightLog.filterNot { it.id == id }
                    }
                )
            }

            else -> {
                LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentPadding = PaddingValues(12.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    item {
                        Card(
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.White),
                            border = BorderStroke(1.dp, SlateCardBorder),
                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(14.dp)) {
                                if (activeMode == AppMode.DIETOLOGIST_ORIGINAL) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column(modifier = Modifier.weight(1f).padding(end = 6.dp)) {
                                            Text(
                                                text = "📅 Giorni della Dieta (G1 - G7)",
                                                fontWeight = FontWeight.ExtraBold,
                                                fontSize = 14.sp,
                                                color = SlateTextDark
                                            )
                                            Text(
                                                text = "Memoria settimanale Lun-Dom • Reset automatico ogni Lunedì",
                                                fontSize = 10.sp,
                                                color = SlateTextMuted
                                            )
                                        }
                                        val scaledOil = FoodItem.smartRoundGrams(currentPlanDay.baseOilGrams * scaleFactor)
                                        Surface(
                                            color = AmberLight,
                                            shape = RoundedCornerShape(50),
                                            border = BorderStroke(1.dp, Color(0xFFFDE68A))
                                        ) {
                                            Text(
                                                text = "Olio: ${scaledOil}g",
                                                color = AmberColor,
                                                fontSize = 11.sp,
                                                fontWeight = FontWeight.ExtraBold,
                                                maxLines = 1,
                                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp)
                                            )
                                        }
                                    }

                                    Spacer(modifier = Modifier.height(8.dp))

                                    // Chip assegnazione Oggi o Ieri (permanente per tutta la settimana Lun-Dom)
                                    val currentAssignedDate = assignedDaysMap[selectedDayNum]
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        if (currentAssignedDate == null) {
                                            Surface(
                                                color = EmeraldLight,
                                                shape = RoundedCornerShape(8.dp),
                                                border = BorderStroke(1.dp, Color(0xFFA7F3D0)),
                                                modifier = Modifier.clickable { markCurrentDayAsAssigned(todayDateStr) }
                                            ) {
                                                Text(
                                                    text = "+ Imposta G$selectedDayNum per Oggi ($todayDateStr)",
                                                    color = EmeraldDark,
                                                    fontSize = 11.sp,
                                                    fontWeight = FontWeight.ExtraBold,
                                                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                                )
                                            }
                                            Surface(
                                                color = BlueLight,
                                                shape = RoundedCornerShape(8.dp),
                                                border = BorderStroke(1.dp, BlueColor.copy(alpha = 0.35f)),
                                                modifier = Modifier.clickable { markCurrentDayAsAssigned(yesterdayDateStr) }
                                            ) {
                                                Text(
                                                    text = "+ Ieri ($yesterdayDateStr)",
                                                    color = BlueColor,
                                                    fontSize = 11.sp,
                                                    fontWeight = FontWeight.ExtraBold,
                                                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                                )
                                            }
                                        } else {
                                            Surface(
                                                color = AmberLight,
                                                shape = RoundedCornerShape(8.dp),
                                                border = BorderStroke(1.dp, Color(0xFFFDE68A)),
                                                modifier = Modifier.clickable {
                                                    assignedDaysMap = assignedDaysMap.toMutableMap().apply { remove(selectedDayNum) }
                                                }
                                            ) {
                                                Text(
                                                    text = "✓ G$selectedDayNum svolto il $currentAssignedDate  ✕ Rimuovi data",
                                                    color = AmberColor,
                                                    fontSize = 11.sp,
                                                    fontWeight = FontWeight.ExtraBold,
                                                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                                )
                                            }
                                        }
                                    }

                                    Spacer(modifier = Modifier.height(10.dp))

                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                                    ) {
                                        DietRepository.planDays.forEach { day ->
                                            val isSelected = day.dayNum == selectedDayNum
                                            val assignedDate = assignedDaysMap[day.dayNum]
                                            val isAssigned = assignedDate != null

                                            Surface(
                                                color = when {
                                                    isSelected -> EmeraldPrimary
                                                    isAssigned -> EmeraldLight
                                                    else -> SlateBg
                                                },
                                                shape = RoundedCornerShape(10.dp),
                                                border = BorderStroke(
                                                    1.dp,
                                                    when {
                                                        isSelected -> EmeraldPrimary
                                                        isAssigned -> Color(0xFFA7F3D0)
                                                        else -> SlateCardBorder
                                                    }
                                                ),
                                                modifier = Modifier
                                                    .weight(1f)
                                                    .clickable { selectOriginalDay(day.dayNum) }
                                            ) {
                                                Column(
                                                    horizontalAlignment = Alignment.CenterHorizontally,
                                                    verticalArrangement = Arrangement.Center,
                                                    modifier = Modifier.padding(vertical = 6.dp)
                                                ) {
                                                    Text(
                                                        text = "G${day.dayNum}",
                                                        fontWeight = FontWeight.ExtraBold,
                                                        fontSize = 13.sp,
                                                        color = when {
                                                            isSelected -> Color.White
                                                            isAssigned -> EmeraldDark
                                                            else -> SlateTextDark
                                                        }
                                                    )
                                                    if (assignedDate != null) {
                                                        Text(
                                                            text = assignedDate,
                                                            fontSize = 9.sp,
                                                            fontWeight = FontWeight.ExtraBold,
                                                            color = if (isSelected) Color.White.copy(alpha = 0.9f) else EmeraldDark,
                                                            maxLines = 1
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                } else {
                                    Text(
                                        text = "✨ Generatore Combinazioni Smart AI",
                                        fontWeight = FontWeight.ExtraBold,
                                        fontSize = 15.sp,
                                        color = PurpleColor
                                    )
                                    Spacer(modifier = Modifier.height(8.dp))
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                    ) {
                                        Button(
                                            onClick = { generateNewSmartDay(false) },
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = if (!smartLowCarbLunch) PurpleColor else SlateTextMuted
                                            ),
                                            modifier = Modifier.weight(1f)
                                        ) {
                                            Text("🍝 Primo + Sugo", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                                        }
                                        Button(
                                            onClick = { generateNewSmartDay(true) },
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = if (smartLowCarbLunch) PurpleColor else SlateTextMuted
                                            ),
                                            modifier = Modifier.weight(1f)
                                        ) {
                                            Text("🥗 Secondo & Verdure", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                                        }
                                    }
                                }

                                Spacer(modifier = Modifier.height(10.dp))

                                Button(
                                    onClick = {
                                        val cleanText = MealShareFormatter.formatFullDay(
                                            dayLabel = currentPlanDay.label,
                                            mealsMap = mealsMap,
                                            waterGlasses = waterGlasses
                                        )
                                        activeShareContent = "Menu ${currentPlanDay.label}" to cleanText
                                    },
                                    colors = ButtonDefaults.buttonColors(containerColor = BlueColor),
                                    shape = RoundedCornerShape(10.dp),
                                    modifier = Modifier.fillMaxWidth().height(38.dp)
                                ) {
                                    Text(
                                        text = "📤 Condividi Intero Giorno (Solo Pasti Scelti)",
                                        color = Color.White,
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                        maxLines = 1
                                    )
                                }
                            }
                        }
                    }

                    // 2. SCHEDA DI RIEPILOGO & CRUSCOTTO CONTACALORIE
                    item {
                        Card(
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.White),
                            border = BorderStroke(1.5.dp, EmeraldPrimary.copy(alpha = 0.4f)),
                            elevation = CardDefaults.cardElevation(defaultElevation = 3.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(14.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text("📊 RIEPILOGO BILANCIO CALORICO", fontSize = 11.sp, fontWeight = FontWeight.Black, color = SlateTextDark)
                                    Surface(color = BlueLight, shape = RoundedCornerShape(50)) {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Text(
                                                text = "◀",
                                                color = BlueColor,
                                                fontSize = 12.sp,
                                                modifier = Modifier
                                                    .clickable { healthDateOffset-- }
                                                    .padding(horizontal = 8.dp, vertical = 4.dp)
                                            )
                                            Text(
                                                text = "⌚ ${if (healthDateOffset == 0) "Oggi" else getRelativeDateLabel(healthDateOffset)}: +$activeCaloriesFromWatch kcal",
                                                color = BlueColor,
                                                fontSize = 9.sp,
                                                fontWeight = FontWeight.ExtraBold,
                                                maxLines = 1
                                            )
                                            Text(
                                                text = "▶",
                                                color = if (healthDateOffset < 0) BlueColor else SlateCardBorder,
                                                fontSize = 12.sp,
                                                modifier = Modifier
                                                    .clickable(enabled = healthDateOffset < 0) { healthDateOffset++ }
                                                    .padding(horizontal = 8.dp, vertical = 4.dp)
                                            )
                                        }
                                    }
                                }

                                Spacer(modifier = Modifier.height(10.dp))

                                val displayTargetCal = lockedTargetKcal ?: prescribedCal
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                                ) {
                                    SummaryMetricTile(
                                        title = "📋 PREVISTE",
                                        value = "$prescribedCal",
                                        unit = "kcal",
                                        subtitle = if (lockedTargetKcal != null) "Tetto: $displayTargetCal kcal" else "Target Dieta",
                                        color = SlateTextDark,
                                        modifier = Modifier.weight(1f)
                                    )

                                    SummaryMetricTile(
                                        title = "🍽️ INGERITE",
                                        value = "$eatenCal",
                                        unit = "kcal",
                                        subtitle = "$consumedCount portate",
                                        color = if (eatenCal <= prescribedCal) EmeraldPrimary else RoseColor,
                                        modifier = Modifier.weight(1f)
                                    )

                                    val remainingCal = prescribedCal - eatenCal
                                    SummaryMetricTile(
                                        title = "🎯 RIMANENTI",
                                        value = if (remainingCal >= 0) "$remainingCal" else "0",
                                        unit = "kcal",
                                        subtitle = if (remainingCal >= 0) "Da mangiare" else "Superate +${abs(remainingCal)}",
                                        color = if (remainingCal >= 0) BlueColor else RoseColor,
                                        modifier = Modifier.weight(1f)
                                    )
                                }

                                Spacer(modifier = Modifier.height(10.dp))

                                Surface(
                                    color = SlateBg,
                                    shape = RoundedCornerShape(10.dp),
                                    border = BorderStroke(1.dp, SlateCardBorder),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Row(
                                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp).fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(
                                                text = "🔥 Dispendio Totale: $totalOutputCal kcal",
                                                fontSize = 11.sp,
                                                fontWeight = FontWeight.ExtraBold,
                                                color = SlateTextDark
                                            )
                                            Text(
                                                text = "BMR ${metabolicReport.bmrKcal} kcal + Attività Orologio $activeCaloriesFromWatch kcal",
                                                fontSize = 9.sp,
                                                color = SlateTextMuted
                                            )
                                        }

                                        Surface(
                                            color = if (realDeficit >= 0) EmeraldLight else RoseLight,
                                            shape = RoundedCornerShape(8.dp),
                                            border = BorderStroke(1.dp, if (realDeficit >= 0) Color(0xFFA7F3D0) else Color(0xFFFECDD3))
                                        ) {
                                            Text(
                                                text = if (realDeficit >= 0) "Deficit: -$realDeficit kcal" else "Surplus: +${abs(realDeficit)} kcal",
                                                color = if (realDeficit >= 0) EmeraldDark else RoseColor,
                                                fontSize = 11.sp,
                                                fontWeight = FontWeight.Black,
                                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                            )
                                        }
                                    }
                                }

                                Spacer(modifier = Modifier.height(12.dp))

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    OutlinedButton(
                                        onClick = {
                                            markCurrentDayAsAssigned(todayDateStr)
                                            mealsMap = mealsMap.mapValues { (_, list) ->
                                                list.map { it.copy(isConsumed = true, consumedGrams = it.currentGrams) }
                                            }
                                        },
                                        shape = RoundedCornerShape(10.dp),
                                        modifier = Modifier.weight(1f).height(36.dp),
                                        contentPadding = PaddingValues(horizontal = 6.dp, vertical = 2.dp)
                                    ) {
                                        Text("✓ Segna Tutto", fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                                    }
                                    OutlinedButton(
                                        onClick = {
                                            mealsMap = mealsMap.mapValues { (_, list) ->
                                                list.filterNot { it.isExtraItem }.map {
                                                    it.copy(isConsumed = false, consumedGrams = it.currentGrams)
                                                }
                                            }
                                        },
                                        shape = RoundedCornerShape(10.dp),
                                        modifier = Modifier.weight(1f).height(36.dp),
                                        contentPadding = PaddingValues(horizontal = 6.dp, vertical = 2.dp)
                                    ) {
                                        Text("↺ Azzera Oggi", fontSize = 11.sp, fontWeight = FontWeight.Bold, color = RoseColor, maxLines = 1)
                                    }
                                }

                                Spacer(modifier = Modifier.height(12.dp))

                                MacroProgressBar("Carboidrati Ingeriti", eatenCarb, prescribedCarb, AmberColor)
                                Spacer(modifier = Modifier.height(6.dp))
                                MacroProgressBar("Proteine Ingerite", eatenProt, prescribedProt, BlueColor)
                                Spacer(modifier = Modifier.height(6.dp))
                                MacroProgressBar("Grassi Ingeriti (incl. Condimenti & Olio)", eatenFat, prescribedFat, RoseColor)

                                Spacer(modifier = Modifier.height(12.dp))

                                Surface(
                                    color = EmeraldLight,
                                    shape = RoundedCornerShape(12.dp),
                                    border = BorderStroke(1.dp, Color(0xFFA7F3D0)),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Column(modifier = Modifier.padding(10.dp)) {
                                        Text(
                                            text = if (lockedTargetKcal != null) {
                                                "🔒 TARGET BLOCCATO SU TUTTI I GIORNI (G1-G7): MAX $lockedTargetKcal KCAL"
                                            } else {
                                                "⚖️ SCALA LE PORZIONI DEL PIANO SUL TUO TARGET KCAL (VALE PER G1-G7):"
                                            },
                                            fontSize = 10.sp,
                                            fontWeight = FontWeight.ExtraBold,
                                            color = EmeraldDark
                                        )
                                        Spacer(modifier = Modifier.height(8.dp))

                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            OutlinedButton(
                                                onClick = {
                                                    val cur = targetCalInput.toIntOrNull() ?: currentPlanDay.baseTotalCal
                                                    applyCalorieRescaleToAllDays((cur - 100).coerceAtLeast(800))
                                                },
                                                shape = RoundedCornerShape(10.dp),
                                                modifier = Modifier.weight(1f).height(44.dp)
                                            ) {
                                                Text("-100 kcal", fontSize = 12.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
                                            }

                                            OutlinedTextField(
                                                value = targetCalInput,
                                                onValueChange = { newValue ->
                                                    targetCalInput = newValue.filter { it.isDigit() }
                                                },
                                                singleLine = true,
                                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                                modifier = Modifier.weight(1.2f).height(52.dp)
                                            )

                                            OutlinedButton(
                                                onClick = {
                                                    val cur = targetCalInput.toIntOrNull() ?: currentPlanDay.baseTotalCal
                                                    applyCalorieRescaleToAllDays((cur + 100).coerceAtMost(4000))
                                                },
                                                shape = RoundedCornerShape(10.dp),
                                                modifier = Modifier.weight(1f).height(44.dp)
                                            ) {
                                                Text("+100 kcal", fontSize = 12.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
                                            }
                                        }

                                        Spacer(modifier = Modifier.height(8.dp))

                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Button(
                                                onClick = {
                                                    val target = targetCalInput.toIntOrNull() ?: currentPlanDay.baseTotalCal
                                                    applyCalorieRescaleToAllDays(target)
                                                },
                                                colors = ButtonDefaults.buttonColors(containerColor = EmeraldPrimary),
                                                shape = RoundedCornerShape(10.dp),
                                                modifier = Modifier.weight(1.4f).height(38.dp)
                                            ) {
                                                Text("✓ Fissa su G1-G7", color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
                                            }

                                            Button(
                                                onClick = { resetToOriginalWeights() },
                                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFBBF24)),
                                                shape = RoundedCornerShape(10.dp),
                                                modifier = Modifier.weight(1f).height(38.dp)
                                            ) {
                                                Text("↺ 100% Base", color = SlateTextDark, fontSize = 12.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // 3. SEZIONI PASTI
                    MealSectionType.entries.forEach { section ->
                        val sectionItems = mealsMap[section] ?: emptyList()
                        val sectionPrescribedCal = sectionItems.filter { !it.isExtraItem }.sumOf { it.currentCal }
                        val sectionEatenCal = sectionItems.sumOf { it.eatenCal }

                        item {
                            Card(
                                shape = RoundedCornerShape(16.dp),
                                colors = CardDefaults.cardColors(containerColor = Color.White),
                                border = BorderStroke(1.dp, SlateCardBorder),
                                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Column(modifier = Modifier.padding(14.dp)) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column(modifier = Modifier.weight(1f).padding(end = 6.dp)) {
                                            Text(
                                                text = "${section.icon} ${section.title}",
                                                fontWeight = FontWeight.Black,
                                                fontSize = 15.sp,
                                                color = SlateTextDark,
                                                maxLines = 1,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                            Text(
                                                text = "Ingerite: $sectionEatenCal / $sectionPrescribedCal kcal",
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 11.sp,
                                                color = EmeraldPrimary,
                                                maxLines = 1
                                            )
                                        }
                                        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                            OutlinedButton(
                                                onClick = {
                                                    val cleanMealText = MealShareFormatter.formatSingleMeal(
                                                        dayLabel = currentPlanDay.label,
                                                        section = section,
                                                        items = sectionItems
                                                    )
                                                    activeShareContent = "${section.title} (${currentPlanDay.label})" to cleanMealText
                                                },
                                                shape = RoundedCornerShape(8.dp),
                                                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 2.dp),
                                                modifier = Modifier.height(32.dp)
                                            ) {
                                                Text("📤 Condividi", fontSize = 10.sp, fontWeight = FontWeight.Bold, color = BlueColor, maxLines = 1)
                                            }

                                            OutlinedButton(
                                                onClick = { activeAddExtraSection = section },
                                                shape = RoundedCornerShape(8.dp),
                                                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 2.dp),
                                                modifier = Modifier.height(32.dp)
                                            ) {
                                                Text("+ Extra", fontSize = 10.sp, fontWeight = FontWeight.Bold, color = PurpleColor, maxLines = 1)
                                            }
                                        }
                                    }

                                    HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), color = SlateCardBorder)

                                    sectionItems.forEachIndexed { idx, food ->
                                        FoodItemRow(
                                            food = food,
                                            onToggleConsumedQuick = {
                                                markCurrentDayAsAssigned(todayDateStr)
                                                val toggled = food.copy(isConsumed = !food.isConsumed)
                                                val mutableList = sectionItems.toMutableList()
                                                mutableList[idx] = toggled
                                                mealsMap = mealsMap.toMutableMap().apply { put(section, mutableList) }
                                            },
                                            onOpenConsumeGramsModal = {
                                                activeConsumeRequest = Triple(section, idx, food)
                                            },
                                            onOpenSwap = { activeSwapRequest = Triple(section, idx, food) },
                                            onRestoreOriginal = if (food.isSwappedFromOriginal) {
                                                { restoreSingleOriginalItem(section, idx) }
                                            } else null,
                                            onOpenRecipe = { recipeId ->
                                                activeRecipeModalId = recipeId
                                                showAllRecipesModal = true
                                            },
                                            onRemoveExtra = if (food.isExtraItem) {
                                                {
                                                    val mutableList = sectionItems.toMutableList()
                                                    mutableList.removeAt(idx)
                                                    mealsMap = mealsMap.toMutableMap().apply { put(section, mutableList) }
                                                }
                                            } else null
                                        )
                                        if (idx < sectionItems.lastIndex) {
                                            HorizontalDivider(modifier = Modifier.padding(vertical = 10.dp), color = Color(0xFFF1F5F9))
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // 4. TRACKER ACQUA NATURALE
                    item {
                        Card(
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.White),
                            border = BorderStroke(1.dp, SlateCardBorder),
                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(14.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text("💧 Acqua Naturale Giornaliera", fontWeight = FontWeight.ExtraBold, fontSize = 14.sp, color = SlateTextDark)
                                        Text("$waterGlasses / $targetWaterGlasses bicchieri (${waterGlasses * 250} ml)", fontSize = 12.sp, color = SlateTextMuted)
                                    }
                                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                        OutlinedButton(
                                            onClick = { waterGlasses = (waterGlasses - 1).coerceAtLeast(0) },
                                            modifier = Modifier.height(36.dp)
                                        ) { Text("-", fontWeight = FontWeight.Bold) }
                                        Button(
                                            onClick = { waterGlasses = (waterGlasses + 1).coerceAtMost(20) },
                                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0284C7)),
                                            modifier = Modifier.height(36.dp)
                                        ) {
                                            Text("+ Bicchiere", color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    activeShareContent?.let { (shareTitle, formattedText) ->
        ShareCleanMenuDialog(
            title = shareTitle,
            initialText = formattedText,
            onDismiss = { activeShareContent = null }
        )
    }

    activeConsumeRequest?.let { (section, itemIdx, currentFood) ->
        ConsumeFoodDialog(
            food = currentFood,
            onDismiss = { activeConsumeRequest = null },
            onConfirmConsumed = { eatenGrams ->
                markCurrentDayAsAssigned(todayDateStr)
                val updated = currentFood.copy(
                    isConsumed = eatenGrams > 0,
                    consumedGrams = eatenGrams
                )
                val mutableList = (mealsMap[section] ?: emptyList()).toMutableList()
                if (itemIdx in mutableList.indices) {
                    mutableList[itemIdx] = updated
                    mealsMap = mealsMap.toMutableMap().apply { put(section, mutableList) }
                }
                activeConsumeRequest = null
            }
        )
    }

    activeAddExtraSection?.let { section ->
        AddExtraFoodDialog(
            section = section,
            onDismiss = { activeAddExtraSection = null },
            onAddExtra = { extraFood ->
                markCurrentDayAsAssigned(todayDateStr)
                val mutableList = (mealsMap[section] ?: emptyList()).toMutableList()
                mutableList.add(extraFood)
                mealsMap = mealsMap.toMutableMap().apply { put(section, mutableList) }
                activeAddExtraSection = null
            }
        )
    }

    // SOSTITUZIONE ISOCALORICA PRECISA AL MILLIMETRO SU TUTTE LE CATEGORIE (BASE, SMART AI E MIX)
    activeSwapRequest?.let { (section, itemIdx, currentFood) ->
        val officialGroup = currentFood.swapGroupId?.let { DietRepository.swapGroups[it] }
        val smartOptions = remember(currentFood) {
            SmartChefEngine.generateIsocaloricAlternatives(currentFood)
        }

        SwapAlternativeDialog(
            currentFood = currentFood,
            officialGroup = officialGroup,
            smartOptions = smartOptions,
            defaultToSmartTab = activeMode == AppMode.SMART_AI_PROPOSALS || officialGroup == null,
            onDismiss = { activeSwapRequest = null },
            onSelectOption = { chosenOpt ->
                val updatedFood = currentFood.applySwappedOption(chosenOpt)
                val mutableList = (mealsMap[section] ?: emptyList()).toMutableList()
                if (itemIdx in mutableList.indices) {
                    mutableList[itemIdx] = updatedFood
                    mealsMap = mealsMap.toMutableMap().apply { put(section, mutableList) }
                }
                activeSwapRequest = null
            }
        )
    }

    if (showAllRecipesModal) {
        RecipesDialog(
            highlightRecipeId = activeRecipeModalId,
            onDismiss = {
                showAllRecipesModal = false
                activeRecipeModalId = null
            }
        )
    }

    if (showCloudSyncModal) {
        val currentStateSnapshot = CloudAppState(
            selectedDayNum = selectedDayNum,
            scaleFactor = scaleFactor,
            targetCalInput = targetCalInput,
            waterGlasses = waterGlasses,
            userProfile = userProfile,
            weightLog = weightLog,
            allDaysMeals = allDaysMeals,
            assignedDaysMap = assignedDaysMap,
            weekKey = currentWeekKey
        )
        CloudSyncDialog(
            currentState = currentStateSnapshot,
            onDismiss = { showCloudSyncModal = false },
            onPushCloudNow = {
                coroutineScope.launch {
                    val ok = FirebaseSyncManager.pushToFirebase(currentStateSnapshot)
                    syncStatusBanner = if (ok) "☁️ Inviato a Firebase con successo ✓" else "⚠️ Errore invio Firebase"
                }
            },
            onPullCloudNow = {
                coroutineScope.launch {
                    val remote = FirebaseSyncManager.pullFromFirebase()
                    if (remote != null) {
                        selectedDayNum = remote.selectedDayNum
                        currentPlanDay = DietRepository.planDays.find { it.dayNum == remote.selectedDayNum }
                            ?: DietRepository.planDays.first()
                        scaleFactor = remote.scaleFactor
                        targetCalInput = remote.targetCalInput
                        lockedTargetKcal = remote.targetCalInput.toIntOrNull()?.takeIf { abs(remote.scaleFactor - 1.0) >= 0.01 }
                        waterGlasses = remote.waterGlasses
                        userProfile = remote.userProfile
                        weightLog = remote.weightLog
                        allDaysMeals = remote.allDaysMeals
                        mealsMap = allDaysMeals[remote.selectedDayNum] ?: currentPlanDay.meals
                        assignedDaysMap = remote.assignedDaysMap.filterValues { it in validWeekDates }
                        currentWeekKey = mondayWeekKey
                        syncStatusBanner = "☁️ Scaricato da Firebase con successo ✓"
                        showCloudSyncModal = false
                    }
                }
            },
            onRestoreState = { restored ->
                selectedDayNum = restored.selectedDayNum
                currentPlanDay = DietRepository.planDays.find { it.dayNum == restored.selectedDayNum }
                    ?: DietRepository.planDays.first()
                scaleFactor = restored.scaleFactor
                targetCalInput = restored.targetCalInput
                lockedTargetKcal = restored.targetCalInput.toIntOrNull()?.takeIf { abs(restored.scaleFactor - 1.0) >= 0.01 }
                waterGlasses = restored.waterGlasses
                userProfile = restored.userProfile
                weightLog = restored.weightLog
                allDaysMeals = restored.allDaysMeals
                mealsMap = allDaysMeals[restored.selectedDayNum] ?: currentPlanDay.meals
                assignedDaysMap = restored.assignedDaysMap.filterValues { it in validWeekDates }
                currentWeekKey = mondayWeekKey
                syncStatusBanner = "☁️ Stato ripristinato con successo!"
                showCloudSyncModal = false
            }
        )
    }
}

@Composable
private fun ShareCleanMenuDialog(
    title: String,
    initialText: String,
    onDismiss: () -> Unit
) {
    var editableText by remember(initialText) { mutableStateOf(initialText) }
    var copiedFeedback by remember { mutableStateOf("") }
    val clipboardManager = LocalClipboardManager.current
    val uriHandler = LocalUriHandler.current

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(18.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "📤 Condividi $title",
                            fontSize = 16.sp,
                            fontWeight = FontWeight.ExtraBold,
                            color = SlateTextDark
                        )
                        Text(
                            text = "Solo i piatti scelti • Senza varianti del dietologo",
                            fontSize = 11.sp,
                            color = EmeraldDark,
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                    TextButton(onClick = onDismiss) {
                        Text("✕", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                OutlinedTextField(
                    value = editableText,
                    onValueChange = { editableText = it },
                    label = { Text("Messaggio pronto da inviare (modificabile)") },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(210.dp)
                )

                if (copiedFeedback.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(6.dp))
                    Text(
                        text = copiedFeedback,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = EmeraldPrimary
                    )
                }

                Spacer(modifier = Modifier.height(12.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Button(
                        onClick = {
                            clipboardManager.setText(AnnotatedString(editableText))
                            platformShareText(title, editableText)
                            copiedFeedback = "✓ Menu di condivisione aperto (e testo copiato)!"
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = EmeraldPrimary),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("📲 Invia (App / Mail)", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
                    }

                    Button(
                        onClick = {
                            clipboardManager.setText(AnnotatedString(editableText))
                            val encoded = editableText
                                .replace("%", "%25")
                                .replace("\n", "%0A")
                                .replace(" ", "%20")
                                .replace("&", "%26")
                            try {
                                uriHandler.openUri("https://wa.me/?text=$encoded")
                            } catch (e: Exception) {
                                platformShareText(title, editableText)
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF25D366)),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("💬 WhatsApp", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedButton(
                        onClick = {
                            val subj = title.replace(" ", "%20")
                            val body = editableText
                                .replace("\n", "%0A")
                                .replace(" ", "%20")
                            try {
                                uriHandler.openUri("mailto:?subject=$subj&body=$body")
                            } catch (e: Exception) {
                                platformShareText(title, editableText)
                            }
                        },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("✉️ Invia per Email", fontSize = 11.sp, fontWeight = FontWeight.Bold, color = BlueColor, maxLines = 1)
                    }

                    OutlinedButton(
                        onClick = {
                            clipboardManager.setText(AnnotatedString(editableText))
                            copiedFeedback = "✓ Testo copiato negli appunti!"
                        },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("📋 Copia Testo", fontSize = 11.sp, fontWeight = FontWeight.Bold, color = SlateTextDark, maxLines = 1)
                    }
                }
            }
        }
    }
}

@Composable
private fun NavTabButton(
    label: String,
    isSelected: Boolean,
    activeBg: Color,
    activeText: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    Surface(
        color = if (isSelected) activeBg else Color.White.copy(alpha = 0.14f),
        shape = RoundedCornerShape(10.dp),
        modifier = modifier.clickable { onClick() }
    ) {
        Box(contentAlignment = Alignment.Center, modifier = Modifier.padding(vertical = 8.dp)) {
            Text(
                text = label,
                fontWeight = FontWeight.ExtraBold,
                fontSize = 11.sp,
                color = if (isSelected) activeText else Color.White,
                maxLines = 1
            )
        }
    }
}

@Composable
private fun FoodItemRow(
    food: FoodItem,
    onToggleConsumedQuick: () -> Unit,
    onOpenConsumeGramsModal: () -> Unit,
    onOpenSwap: () -> Unit,
    onRestoreOriginal: (() -> Unit)? = null,
    onOpenRecipe: (String) -> Unit,
    onRemoveExtra: (() -> Unit)? = null
) {
    val (badgeBg, badgeText) = when (food.slotType) {
        MealSlotType.PRIMO, MealSlotType.OLIO_EXTRA -> AmberLight to AmberColor
        MealSlotType.CONDIMENTO_PRIMO -> PurpleLight to PurpleColor
        MealSlotType.SECONDO -> RoseLight to RoseColor
        MealSlotType.CONTORNO -> EmeraldLight to EmeraldDark
        MealSlotType.FRUTTA -> PurpleLight to PurpleColor
        else -> BlueLight to BlueColor
    }

    val compactSlotLabel = when (food.slotType) {
        MealSlotType.COLAZIONE_BEVANDA -> "BEVANDA / YOGURT"
        MealSlotType.COLAZIONE_CEREALI -> "CEREALI / FETTE / BISCOTTI"
        MealSlotType.PRIMO -> "PRIMO (BASE PASTA / RISO)"
        MealSlotType.CONDIMENTO_PRIMO -> "CONDIMENTO / SUGO"
        MealSlotType.SECONDO -> "SECONDO PIATTO"
        MealSlotType.CONTORNO -> "CONTORNO / VERDURE"
        MealSlotType.PANE -> "QUOTA PANE"
        MealSlotType.FRUTTA -> "FRUTTA"
        MealSlotType.OLIO_EXTRA -> "OLIO EVO / EXTRA"
    }

    val diffGrams = food.consumedGrams != food.currentGrams

    Surface(
        color = if (food.isConsumed) EmeraldLight.copy(alpha = 0.45f) else Color.Transparent,
        shape = RoundedCornerShape(12.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(8.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth()
            ) {
                Surface(
                    color = badgeBg,
                    shape = RoundedCornerShape(6.dp),
                    modifier = Modifier.weight(1f, fill = false).padding(end = 8.dp)
                ) {
                    Text(
                        text = if (food.isExtraItem) "EXTRA FUORI PIANO" else compactSlotLabel,
                        color = badgeText,
                        fontSize = 9.sp,
                        fontWeight = FontWeight.ExtraBold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                    )
                }

                Surface(
                    color = if (food.isConsumed) EmeraldPrimary else SlateBg,
                    shape = RoundedCornerShape(50),
                    border = BorderStroke(1.dp, if (food.isConsumed) EmeraldPrimary else SlateCardBorder),
                    modifier = Modifier.clickable { onToggleConsumedQuick() }
                ) {
                    Text(
                        text = if (food.isConsumed) "✓ MANGIATO (${food.eatenCal} kcal)" else "○ Segna Mangiato",
                        color = if (food.isConsumed) Color.White else SlateTextDark,
                        fontSize = 10.sp,
                        fontWeight = FontWeight.ExtraBold,
                        maxLines = 1,
                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp)
                    )
                }
            }

            if (food.isSwappedFromOriginal) {
                Spacer(modifier = Modifier.height(6.dp))
                Surface(
                    color = AmberLight,
                    shape = RoundedCornerShape(8.dp),
                    border = BorderStroke(1.dp, Color(0xFFFDE68A)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 5.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "📌 Dietologo: ${food.originalPrescribedName} (${food.originalPrescribedGrams}g • ${food.originalPrescribedCal} kcal base)",
                            fontSize = 10.sp,
                            fontWeight = FontWeight.ExtraBold,
                            color = AmberColor,
                            modifier = Modifier.weight(1f)
                        )
                        if (onRestoreOriginal != null) {
                            Surface(
                                color = Color.White,
                                shape = RoundedCornerShape(6.dp),
                                border = BorderStroke(1.dp, EmeraldPrimary),
                                modifier = Modifier
                                    .padding(start = 8.dp)
                                    .clickable { onRestoreOriginal() }
                            ) {
                                Text(
                                    text = "↺ Ripristina",
                                    fontSize = 10.sp,
                                    fontWeight = FontWeight.Black,
                                    color = EmeraldDark,
                                    maxLines = 1,
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                )
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(6.dp))

            Text(
                text = food.name,
                fontWeight = FontWeight.ExtraBold,
                fontSize = 15.sp,
                color = SlateTextDark,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(5.dp))

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                if (!food.isExtraItem) {
                    Surface(color = SlateTextDark, shape = RoundedCornerShape(6.dp)) {
                        Text(
                            text = "Dose: ${food.currentGrams}g",
                            color = Color.White,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.ExtraBold,
                            maxLines = 1,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                        )
                    }
                }

                if (food.isConsumed) {
                    Surface(
                        color = if (diffGrams) AmberColor else EmeraldPrimary,
                        shape = RoundedCornerShape(6.dp)
                    ) {
                        Text(
                            text = "Ingeriti: ${food.consumedGrams}g",
                            color = Color.White,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.ExtraBold,
                            maxLines = 1,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                        )
                    }
                }

                Surface(
                    color = SlateBg,
                    shape = RoundedCornerShape(6.dp),
                    border = BorderStroke(1.dp, SlateCardBorder)
                ) {
                    Text(
                        text = "${food.currentCal} kcal",
                        color = SlateTextDark,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.ExtraBold,
                        maxLines = 1,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                    )
                }
            }

            Spacer(modifier = Modifier.height(4.dp))
            if (food.isConsumed && diffGrams && !food.isExtraItem) {
                Text(
                    text = "🍽️ Ingerito: ${food.eatenCal} kcal (C:${food.eatenCarb}g • P:${food.eatenProt}g • G:${food.eatenFat}g)",
                    fontSize = 11.sp,
                    color = AmberColor,
                    fontWeight = FontWeight.Bold
                )
            } else {
                Text(
                    text = "Macro: Carboidrati ${food.currentCarb}g • Proteine ${food.currentProt}g • Grassi ${food.currentFat}g",
                    fontSize = 11.sp,
                    color = SlateTextMuted,
                    fontWeight = FontWeight.SemiBold
                )
            }

            if (food.compNote.isNotEmpty()) {
                Spacer(modifier = Modifier.height(5.dp))
                Surface(
                    color = EmeraldLight,
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = "📋 ${food.compNote}",
                        fontSize = 11.sp,
                        color = EmeraldDark,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            Row(
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth()
            ) {
                Button(
                    onClick = onOpenConsumeGramsModal,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = if (food.isConsumed) AmberColor else BlueColor
                    ),
                    shape = RoundedCornerShape(8.dp),
                    contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                    modifier = Modifier.weight(1f).height(34.dp)
                ) {
                    Text(
                        text = if (food.isConsumed) "⚖️ Grammi Ingeriti" else "🍽️ Registra Grammi",
                        color = Color.White,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }

                if (!food.isExtraItem && food.slotType != MealSlotType.OLIO_EXTRA) {
                    val swapLabel = when (food.slotType) {
                        MealSlotType.CONDIMENTO_PRIMO -> "⇄ Condimento"
                        MealSlotType.PRIMO -> "⇄ Pasta / Riso"
                        MealSlotType.CONTORNO -> "⇄ Mix / Verdure"
                        MealSlotType.FRUTTA -> "⇄ Mix / Frutta"
                        else -> "⇄ Alternative"
                    }
                    OutlinedButton(
                        onClick = onOpenSwap,
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                        modifier = Modifier.weight(1f).height(34.dp)
                    ) {
                        Text(
                            text = swapLabel,
                            color = EmeraldDark,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.ExtraBold,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }

                if (food.recipeId != null) {
                    OutlinedButton(
                        onClick = { onOpenRecipe(food.recipeId) },
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                        modifier = Modifier.height(34.dp)
                    ) {
                        Text("📖", fontSize = 12.sp)
                    }
                }

                if (onRemoveExtra != null) {
                    OutlinedButton(
                        onClick = onRemoveExtra,
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                        modifier = Modifier.height(34.dp)
                    ) {
                        Text("✕", color = RoseColor, fontSize = 12.sp, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}

@Composable
private fun ConsumeFoodDialog(
    food: FoodItem,
    onDismiss: () -> Unit,
    onConfirmConsumed: (Int) -> Unit
) {
    var eatenGramsText by remember { mutableStateOf(food.consumedGrams.toString()) }
    val parsedGrams = eatenGramsText.toIntOrNull() ?: 0
    val previewFood = food.copy(isConsumed = true, consumedGrams = parsedGrams)

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(18.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text("🍽️ Registra Consumo Reale", fontSize = 16.sp, fontWeight = FontWeight.ExtraBold, color = SlateTextDark)
                Spacer(modifier = Modifier.height(4.dp))
                Text(food.name, fontSize = 14.sp, fontWeight = FontWeight.ExtraBold, color = EmeraldDark)
                Text(
                    text = "Dose prevista: ${food.currentGrams}g (${food.currentCal} kcal) — La dieta NON verrà modificata.",
                    fontSize = 11.sp,
                    color = SlateTextMuted
                )

                Spacer(modifier = Modifier.height(12.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    OutlinedButton(
                        onClick = { eatenGramsText = (food.currentGrams / 2).toString() },
                        contentPadding = PaddingValues(horizontal = 6.dp),
                        modifier = Modifier.weight(1f).height(34.dp)
                    ) { Text("Metà (${food.currentGrams / 2}g)", fontSize = 10.sp, maxLines = 1) }

                    OutlinedButton(
                        onClick = { eatenGramsText = (food.currentGrams - 20).coerceAtLeast(5).toString() },
                        contentPadding = PaddingValues(horizontal = 6.dp),
                        modifier = Modifier.weight(1f).height(34.dp)
                    ) { Text("-20g", fontSize = 10.sp, maxLines = 1) }

                    OutlinedButton(
                        onClick = { eatenGramsText = food.currentGrams.toString() },
                        contentPadding = PaddingValues(horizontal = 6.dp),
                        modifier = Modifier.weight(1f).height(34.dp)
                    ) { Text("Dose (${food.currentGrams}g)", fontSize = 10.sp, fontWeight = FontWeight.Bold, maxLines = 1) }
                }

                Spacer(modifier = Modifier.height(10.dp))

                OutlinedTextField(
                    value = eatenGramsText,
                    onValueChange = { v -> eatenGramsText = v.filter { it.isDigit() } },
                    label = { Text("Grammi realmente mangiati oggi (g)") },
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(modifier = Modifier.height(10.dp))

                Surface(color = EmeraldLight, shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.padding(10.dp)) {
                        Text(
                            text = "Calorie Ingerite calcolate: ${previewFood.eatenCal} kcal",
                            fontWeight = FontWeight.ExtraBold,
                            fontSize = 13.sp,
                            color = EmeraldDark
                        )
                        Text(
                            text = "Carboidrati: ${previewFood.eatenCarb}g • Proteine: ${previewFood.eatenProt}g • Grassi: ${previewFood.eatenFat}g",
                            fontSize = 11.sp,
                            color = SlateTextDark
                        )
                    }
                }

                Spacer(modifier = Modifier.height(14.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("Annulla") }
                    Spacer(modifier = Modifier.width(6.dp))
                    Button(
                        onClick = { onConfirmConsumed(parsedGrams) },
                        colors = ButtonDefaults.buttonColors(containerColor = EmeraldPrimary)
                    ) {
                        Text("✓ Salva nel Contacalorie", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}

@Composable
private fun AddExtraFoodDialog(
    section: MealSectionType,
    onDismiss: () -> Unit,
    onAddExtra: (FoodItem) -> Unit
) {
    var name by remember { mutableStateOf("") }
    var gramsText by remember { mutableStateOf("50") }
    var calPer100Text by remember { mutableStateOf("250") }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(18.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text("➕ Aggiungi Extra in ${section.title}", fontSize = 15.sp, fontWeight = FontWeight.ExtraBold)
                Text(
                    "Si sommerà solo alle calorie ingerite oggi senza toccare la dieta del dietologo.",
                    fontSize = 11.sp,
                    color = SlateTextMuted
                )
                Spacer(modifier = Modifier.height(10.dp))

                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Alimento extra (es. Grissini, Cioccolato, Frutta)") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(8.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = gramsText,
                        onValueChange = { gramsText = it.filter { ch -> ch.isDigit() } },
                        label = { Text("Grammi mangiati") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.weight(1f)
                    )
                    OutlinedTextField(
                        value = calPer100Text,
                        onValueChange = { calPer100Text = it.filter { ch -> ch.isDigit() } },
                        label = { Text("Kcal per 100g") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.weight(1f)
                    )
                }

                Spacer(modifier = Modifier.height(14.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("Annulla") }
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(
                        onClick = {
                            val g = gramsText.toIntOrNull() ?: 50
                            val c100 = calPer100Text.toIntOrNull() ?: 200
                            val totCal = ((c100 * g) / 100.0).roundToInt()
                            val extra = FoodItem(
                                id = "extra_${Random.nextInt(100, 999)}",
                                name = name.ifBlank { "Alimento Extra" },
                                slotType = MealSlotType.OLIO_EXTRA,
                                baseGrams = g,
                                currentGrams = g,
                                baseCal = totCal,
                                baseCarb = (totCal * 0.50) / 4.0,
                                baseProt = (totCal * 0.20) / 4.0,
                                baseFat = (totCal * 0.30) / 9.0,
                                compNote = "Extra fuori piano registrato oggi",
                                isConsumed = true,
                                consumedGrams = g,
                                isExtraItem = true
                            )
                            onAddExtra(extra)
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = PurpleColor)
                    ) {
                        Text("Aggiungi Ingerito", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}

@Composable
private fun WeightDiaryScreen(
    currentWeight: Double,
    targetWeight: Double,
    currentWaist: Int,
    entries: List<WeightEntry>,
    modifier: Modifier = Modifier,
    onAddWeightEntry: (Double, Int, String, String) -> Unit,
    onDeleteEntry: (String) -> Unit
) {
    var newWeightText by remember { mutableStateOf(currentWeight.toString()) }
    var newWaistText by remember { mutableStateOf(currentWaist.toString()) }
    var dateText by remember { mutableStateOf("Oggi") }
    var noteText by remember { mutableStateOf("") }

    val startWeight = entries.lastOrNull()?.weightKg ?: currentWeight
    val lostSoFar = ((startWeight - currentWeight) * 10.0).roundToInt() / 10.0
    val remainingToGoal = ((currentWeight - targetWeight) * 10.0).roundToInt() / 10.0

    LazyColumn(
        modifier = modifier,
        contentPadding = PaddingValues(12.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        item {
            Card(
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                border = BorderStroke(1.5.dp, PurpleColor.copy(alpha = 0.4f)),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    Text("⚖️ Diario del Peso & Girovita", fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = PurpleColor)
                    Spacer(modifier = Modifier.height(10.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        MetricBox("Peso Attuale", "$currentWeight kg", "Aggiornato", SlateTextDark, Modifier.weight(1f))
                        MetricBox(
                            "Variazione",
                            if (lostSoFar >= 0) "-$lostSoFar kg" else "+${abs(lostSoFar)} kg",
                            "Da inizio diario",
                            if (lostSoFar >= 0) EmeraldPrimary else RoseColor,
                            Modifier.weight(1f)
                        )
                        MetricBox("Al Traguardo", "$remainingToGoal kg", "Obiettivo $targetWeight kg", BlueColor, Modifier.weight(1f))
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(
                            value = newWeightText,
                            onValueChange = { newWeightText = it.filter { ch -> ch.isDigit() || ch == '.' } },
                            label = { Text("Nuovo Peso (kg)") },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                        OutlinedTextField(
                            value = newWaistText,
                            onValueChange = { newWaistText = it.filter { ch -> ch.isDigit() } },
                            label = { Text("Girovita (cm)") },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(
                            value = dateText,
                            onValueChange = { dateText = it },
                            label = { Text("Data (es. 16 Ott)") },
                            singleLine = true,
                            modifier = Modifier.weight(1f)
                        )
                        OutlinedTextField(
                            value = noteText,
                            onValueChange = { noteText = it },
                            label = { Text("Nota (es. Digiuno)") },
                            singleLine = true,
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    Button(
                        onClick = {
                            val w = newWeightText.toDoubleOrNull() ?: currentWeight
                            val wst = newWaistText.toIntOrNull() ?: currentWaist
                            onAddWeightEntry(w, wst, dateText.ifBlank { "Oggi" }, noteText)
                            noteText = ""
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = PurpleColor),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("➕ Registra Pesata e Sincronizza Firebase", color = Color.White, fontWeight = FontWeight.ExtraBold)
                    }
                }
            }
        }

        item {
            Text("📜 Storico Pesate Registrate", fontWeight = FontWeight.ExtraBold, fontSize = 14.sp, color = SlateTextDark)
        }

        items(entries) { entry ->
            Card(
                shape = RoundedCornerShape(12.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                border = BorderStroke(1.dp, SlateCardBorder),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(12.dp).fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(entry.dateLabel, fontSize = 11.sp, fontWeight = FontWeight.Bold, color = SlateTextMuted)
                        Text("${entry.weightKg} kg  •  Girovita: ${entry.waistCm} cm", fontSize = 15.sp, fontWeight = FontWeight.ExtraBold, color = SlateTextDark)
                        if (entry.note.isNotBlank()) {
                            Text(entry.note, fontSize = 11.sp, color = EmeraldDark)
                        }
                    }
                    TextButton(onClick = { onDeleteEntry(entry.id) }) {
                        Text("✕", color = RoseColor, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}

@Composable
private fun CloudSyncDialog(
    currentState: CloudAppState,
    onDismiss: () -> Unit,
    onPushCloudNow: () -> Unit,
    onPullCloudNow: () -> Unit,
    onRestoreState: (CloudAppState) -> Unit
) {
    val serializedCurrent = remember(currentState) {
        FirebaseSyncManager.serializeState(currentState)
    }
    var cloudCodeText by remember { mutableStateOf(serializedCurrent) }
    var firebaseUrlText by remember { mutableStateOf(FirebaseSyncManager.firebaseDbUrl) }
    var feedbackMsg by remember { mutableStateOf("") }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(18.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text("☁️ Sincronizzazione Cloud Firebase", fontSize = 16.sp, fontWeight = FontWeight.ExtraBold)
                Text(
                    "Connesso a nutritrack-36ff6 • Sincronizza tutti i 7 Giorni, Date Lun-Dom, Mix, Calorie ingerite e Peso.",
                    fontSize = 11.sp,
                    color = SlateTextMuted
                )

                Spacer(modifier = Modifier.height(10.dp))

                OutlinedTextField(
                    value = firebaseUrlText,
                    onValueChange = {
                        firebaseUrlText = it
                        FirebaseSyncManager.firebaseDbUrl = it
                    },
                    label = { Text("URL Firebase Realtime DB") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(modifier = Modifier.height(8.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Button(
                        onClick = {
                            onPushCloudNow()
                            feedbackMsg = "☁️ Invio dati a Firebase nutritrack-36ff6 in corso..."
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = EmeraldPrimary),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("⬆️ Salva su Cloud", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                    }
                    Button(
                        onClick = {
                            onPullCloudNow()
                            feedbackMsg = "☁️ Lettura dati da Firebase nutritrack-36ff6..."
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = BlueColor),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("⬇️ Scarica da Cloud", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                OutlinedTextField(
                    value = cloudCodeText,
                    onValueChange = { cloudCodeText = it },
                    label = { Text("Codice Backup Diretto (Opzionale)") },
                    maxLines = 3,
                    modifier = Modifier.fillMaxWidth().height(90.dp)
                )

                if (feedbackMsg.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(6.dp))
                    Text(feedbackMsg, fontSize = 11.sp, fontWeight = FontWeight.Bold, color = EmeraldDark)
                }

                Spacer(modifier = Modifier.height(10.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    OutlinedButton(
                        onClick = {
                            val restored = FirebaseSyncManager.deserializeState(cloudCodeText)
                            if (restored != null) onRestoreState(restored)
                            else feedbackMsg = "⚠️ Codice non valido"
                        }
                    ) {
                        Text("Ripristina da Codice", fontSize = 11.sp)
                    }
                    TextButton(onClick = onDismiss) {
                        Text("Chiudi")
                    }
                }
            }
        }
    }
}

@Composable
private fun UserProfileScreen(
    profile: UserProfileData,
    report: MetabolicReport,
    modifier: Modifier = Modifier,
    onUpdateProfile: (UserProfileData) -> Unit,
    onApplyTargetToDiet: (Int, Int) -> Unit
) {
    var ageText by remember(profile.age) { mutableStateOf(profile.age.toString()) }
    var heightText by remember(profile.heightCm) { mutableStateOf(profile.heightCm.toString()) }
    var weightText by remember(profile.currentWeightKg) { mutableStateOf(profile.currentWeightKg.toString()) }
    var targetWeightText by remember(profile.targetWeightKg) { mutableStateOf(profile.targetWeightKg.toString()) }
    var waistText by remember(profile.waistCm) { mutableStateOf(profile.waistCm.toString()) }

    LazyColumn(
        modifier = modifier,
        contentPadding = PaddingValues(12.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        item {
            Card(
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                border = BorderStroke(1.dp, SlateCardBorder),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    Text("👤 I Tuoi Dati Corporei", fontWeight = FontWeight.ExtraBold, fontSize = 15.sp, color = SlateTextDark)
                    Spacer(modifier = Modifier.height(10.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Gender.entries.forEach { g ->
                            val isSel = profile.gender == g
                            Surface(
                                color = if (isSel) EmeraldPrimary else SlateBg,
                                shape = RoundedCornerShape(10.dp),
                                border = BorderStroke(1.dp, if (isSel) EmeraldPrimary else SlateCardBorder),
                                modifier = Modifier.weight(1f).clickable { onUpdateProfile(profile.copy(gender = g)) }
                            ) {
                                Box(contentAlignment = Alignment.Center, modifier = Modifier.padding(vertical = 10.dp)) {
                                    Text(g.label, fontWeight = FontWeight.ExtraBold, fontSize = 13.sp, color = if (isSel) Color.White else SlateTextDark)
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(
                            value = ageText,
                            onValueChange = { v ->
                                ageText = v.filter { it.isDigit() }
                                ageText.toIntOrNull()?.let { onUpdateProfile(profile.copy(age = it)) }
                            },
                            label = { Text("Età (anni)") },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                        OutlinedTextField(
                            value = heightText,
                            onValueChange = { v ->
                                heightText = v.filter { it.isDigit() }
                                heightText.toIntOrNull()?.let { onUpdateProfile(profile.copy(heightCm = it)) }
                            },
                            label = { Text("Altezza (cm)") },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(
                            value = weightText,
                            onValueChange = { v ->
                                weightText = v.filter { it.isDigit() || it == '.' }
                                weightText.toDoubleOrNull()?.let { onUpdateProfile(profile.copy(currentWeightKg = it)) }
                            },
                            label = { Text("Peso Attuale (kg)") },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                        OutlinedTextField(
                            value = targetWeightText,
                            onValueChange = { v ->
                                targetWeightText = v.filter { it.isDigit() || it == '.' }
                                targetWeightText.toDoubleOrNull()?.let { onUpdateProfile(profile.copy(targetWeightKg = it)) }
                            },
                            label = { Text("Peso Obiettivo (kg)") },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = waistText,
                        onValueChange = { v ->
                            waistText = v.filter { it.isDigit() }
                            waistText.toIntOrNull()?.let { onUpdateProfile(profile.copy(waistCm = it)) }
                        },
                        label = { Text("Circonferenza Vita / Girovita (cm)") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        }

        item {
            Card(
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                border = BorderStroke(1.dp, SlateCardBorder),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    Text("🏃 Stile di Vita & Deficit Calorico", fontWeight = FontWeight.ExtraBold, fontSize = 14.sp, color = SlateTextDark)
                    Spacer(modifier = Modifier.height(8.dp))

                    ActivityLevel.entries.forEach { act ->
                        val isSel = profile.activityLevel == act
                        Surface(
                            color = if (isSel) EmeraldLight else SlateBg,
                            shape = RoundedCornerShape(10.dp),
                            border = BorderStroke(1.dp, if (isSel) EmeraldPrimary else SlateCardBorder),
                            modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp).clickable { onUpdateProfile(profile.copy(activityLevel = act)) }
                        ) {
                            Row(modifier = Modifier.padding(10.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(act.label, fontWeight = FontWeight.Bold, fontSize = 13.sp, color = SlateTextDark)
                                    Text(act.description, fontSize = 11.sp, color = SlateTextMuted)
                                }
                                Text("x${act.multiplier}", fontWeight = FontWeight.ExtraBold, fontSize = 12.sp, color = if (isSel) EmeraldDark else SlateTextMuted)
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    DeficitGoal.entries.forEach { goal ->
                        val isSel = profile.deficitGoal == goal
                        Surface(
                            color = if (isSel) BlueLight else SlateBg,
                            shape = RoundedCornerShape(10.dp),
                            border = BorderStroke(1.dp, if (isSel) BlueColor else SlateCardBorder),
                            modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp).clickable { onUpdateProfile(profile.copy(deficitGoal = goal)) }
                        ) {
                            Row(modifier = Modifier.padding(10.dp), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text(goal.label, fontWeight = FontWeight.Bold, fontSize = 13.sp, color = SlateTextDark)
                                if (goal.weeklyKgLoss > 0) {
                                    Text("~ -${goal.weeklyKgLoss} kg/sett.", fontWeight = FontWeight.ExtraBold, fontSize = 12.sp, color = BlueColor)
                                }
                            }
                        }
                    }
                }
            }
        }

        item {
            Card(
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                border = BorderStroke(1.5.dp, EmeraldPrimary),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text("📊 Il Tuo Quadro Metabolico Completo", fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = EmeraldDark)
                    Spacer(modifier = Modifier.height(12.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        MetricBox("BMR (Basale)", "${report.bmrKcal} kcal", "A riposo", SlateTextDark, Modifier.weight(1f))
                        MetricBox("TDEE (Reale)", "${report.tdeeKcal} kcal", "Mantenimento", BlueColor, Modifier.weight(1f))
                        MetricBox("TARGET DIETA", "${report.recommendedDietKcal} kcal", "Dimagrimento", EmeraldPrimary, Modifier.weight(1f))
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        MetricBox("BMI", "${report.bmi}", report.bmiCategory, AmberColor, Modifier.weight(1f))
                        MetricBox("Peso Forma", "${report.idealWeightMinKg}-${report.idealWeightMaxKg}kg", "BMI 18.5-24.9", SlateTextDark, Modifier.weight(1f))
                        MetricBox("Acqua", "${report.recommendedWaterMl} ml", "${report.recommendedWaterGlasses} bicchieri", Color(0xFF0284C7), Modifier.weight(1f))
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    Button(
                        onClick = { onApplyTargetToDiet(report.recommendedDietKcal, report.recommendedWaterGlasses) },
                        colors = ButtonDefaults.buttonColors(containerColor = EmeraldPrimary),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            text = "⚖️ Fissa ${report.recommendedDietKcal} kcal su Tutti i Giorni (G1-G7)",
                            color = Color.White,
                            fontWeight = FontWeight.ExtraBold,
                            fontSize = 13.sp
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MetricBox(title: String, value: String, subtitle: String, color: Color, modifier: Modifier = Modifier) {
    Surface(
        color = SlateBg,
        shape = RoundedCornerShape(12.dp),
        border = BorderStroke(1.dp, SlateCardBorder),
        modifier = modifier
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.padding(vertical = 10.dp, horizontal = 6.dp)) {
            Text(title, fontSize = 10.sp, fontWeight = FontWeight.ExtraBold, color = SlateTextMuted, maxLines = 1)
            Spacer(modifier = Modifier.height(2.dp))
            Text(value, fontSize = 14.sp, fontWeight = FontWeight.Black, color = color, maxLines = 1)
            Text(subtitle, fontSize = 10.sp, color = SlateTextMuted, maxLines = 1)
        }
    }
}

@Composable
private fun MacroProgressBar(label: String, current: Double, target: Int, color: Color) {
    val progress = (current.toFloat() / target.toFloat()).coerceIn(0f, 1f)
    Column {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text("● $label", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = color)
            Text("${current.roundToInt()}g / ${target}g", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = SlateTextDark)
        }
        Spacer(modifier = Modifier.height(3.dp))
        LinearProgressIndicator(
            progress = { progress },
            color = color,
            trackColor = Color(0xFFF1F5F9),
            modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(50))
        )
    }
}

/**
 * POPUP ALTERNATIVE: MOSTRA SEMPRE LA GRAMMATURA ESATTA PER LE CALORIE PREVISTE DALLO SLOT
 * SIA IN "📋 BASE" CHE IN "✨ SMART AI" CHE IN "🥗🍎 MIX (MAX 10)"
 */
@Composable
private fun SwapAlternativeDialog(
    currentFood: FoodItem,
    officialGroup: SwapGroup?,
    smartOptions: List<SwapOption>,
    defaultToSmartTab: Boolean,
    onDismiss: () -> Unit,
    onSelectOption: (SwapOption) -> Unit
) {
    val supportsMultiMix = currentFood.slotType == MealSlotType.CONTORNO || currentFood.slotType == MealSlotType.FRUTTA
    var selectedTab by remember { mutableStateOf(if (defaultToSmartTab || officialGroup == null) 1 else 0) }

    val mixCatalog = remember(currentFood.slotType) {
        SmartChefEngine.smartCatalog.filter { it.targetSlot == currentFood.slotType }
    }
    var selectedMixIds by remember { mutableStateOf(listOf<String>()) }
    var customMixGramsText by remember { mutableStateOf(mapOf<String, String>()) }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(18.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            modifier = Modifier.fillMaxWidth().heightIn(max = 660.dp)
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "CALCOLO DOSE ESATTA PER QUESTO PASTO (${currentFood.currentCal} KCAL)",
                            fontSize = 10.sp,
                            fontWeight = FontWeight.ExtraBold,
                            color = EmeraldPrimary
                        )
                        Text(currentFood.name, fontSize = 15.sp, fontWeight = FontWeight.ExtraBold, color = SlateTextDark)
                    }
                    TextButton(onClick = onDismiss) { Text("✕", fontWeight = FontWeight.Bold, fontSize = 16.sp) }
                }

                Spacer(modifier = Modifier.height(6.dp))
                Surface(
                    color = AmberLight,
                    shape = RoundedCornerShape(8.dp),
                    border = BorderStroke(1.dp, Color(0xFFFDE68A)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = "📌 Budget Slot: ${currentFood.currentCal} kcal (Base Dietologo: ${currentFood.originalPrescribedName} ${currentFood.originalPrescribedGrams}g • ${currentFood.originalPrescribedCal} kcal)",
                        fontSize = 10.sp,
                        fontWeight = FontWeight.Bold,
                        color = AmberColor,
                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                    if (officialGroup != null) {
                        Surface(
                            color = if (selectedTab == 0) EmeraldPrimary else SlateBg,
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.weight(1f).clickable { selectedTab = 0 }
                        ) {
                            Box(contentAlignment = Alignment.Center, modifier = Modifier.padding(vertical = 8.dp)) {
                                Text("📋 Base", fontSize = 10.sp, fontWeight = FontWeight.ExtraBold, color = if (selectedTab == 0) Color.White else SlateTextDark, maxLines = 1)
                            }
                        }
                    }
                    Surface(
                        color = if (selectedTab == 1) PurpleColor else SlateBg,
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.weight(1f).clickable { selectedTab = 1 }
                    ) {
                        Box(contentAlignment = Alignment.Center, modifier = Modifier.padding(vertical = 8.dp)) {
                            Text("✨ Smart AI", fontSize = 10.sp, fontWeight = FontWeight.ExtraBold, color = if (selectedTab == 1) Color.White else SlateTextDark, maxLines = 1)
                        }
                    }
                    if (supportsMultiMix) {
                        Surface(
                            color = if (selectedTab == 2) BlueColor else SlateBg,
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.weight(1.25f).clickable { selectedTab = 2 }
                        ) {
                            Box(contentAlignment = Alignment.Center, modifier = Modifier.padding(vertical = 8.dp)) {
                                Text(
                                    text = "🥗🍎 Mix (Max 10)",
                                    fontSize = 10.sp,
                                    fontWeight = FontWeight.ExtraBold,
                                    color = if (selectedTab == 2) Color.White else SlateTextDark,
                                    maxLines = 1
                                )
                            }
                        }
                    }
                }

                HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))

                if (selectedTab == 2 && supportsMultiMix) {
                    val selectedTemplates = mixCatalog.filter { it.id in selectedMixIds }
                    val parsedCustomGrams = customMixGramsText.mapValues { it.value.toIntOrNull() }
                    val previewMixOption = SmartChefEngine.calculateMultiMixOption(
                        targetFood = currentFood,
                        selectedTemplates = selectedTemplates,
                        customGramsMap = parsedCustomGrams
                    )

                    Text(
                        text = "Spunta fino a 10 ingredienti (${selectedMixIds.size}/10). Lascia 'Auto' o scrivi i grammi a mano nella casella a destra:",
                        fontSize = 11.sp,
                        color = SlateTextMuted,
                        fontWeight = FontWeight.SemiBold
                    )

                    Spacer(modifier = Modifier.height(6.dp))

                    if (previewMixOption != null) {
                        Surface(
                            color = BlueLight,
                            shape = RoundedCornerShape(12.dp),
                            border = BorderStroke(1.5.dp, BlueColor),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(10.dp)) {
                                Text(
                                    text = previewMixOption.name,
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.ExtraBold,
                                    color = BlueColor
                                )
                                Text(
                                    text = "Totale Mix: ${previewMixOption.baseGrams}g • ${previewMixOption.baseCal} kcal (su ${currentFood.currentCal} kcal previste) | C:${previewMixOption.baseCarb}g P:${previewMixOption.baseProt}g G:${previewMixOption.baseFat}g",
                                    fontSize = 10.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = SlateTextDark
                                )
                                Spacer(modifier = Modifier.height(6.dp))
                                Button(
                                    onClick = { onSelectOption(previewMixOption) },
                                    colors = ButtonDefaults.buttonColors(containerColor = EmeraldPrimary),
                                    shape = RoundedCornerShape(8.dp),
                                    modifier = Modifier.fillMaxWidth().height(36.dp),
                                    contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp)
                                ) {
                                    Text(
                                        text = "✓ Applica questo Mix (${selectedMixIds.size} cibi)",
                                        color = Color.White,
                                        fontSize = 11.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                        maxLines = 1
                                    )
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                    }

                    LazyColumn(
                        verticalArrangement = Arrangement.spacedBy(6.dp),
                        modifier = Modifier.weight(1f, fill = false)
                    ) {
                        items(mixCatalog) { tpl ->
                            val isChecked = tpl.id in selectedMixIds
                            val manualText = customMixGramsText[tpl.id] ?: ""

                            val autoAssignedGrams = if (isChecked && selectedTemplates.isNotEmpty()) {
                                val manualVal = manualText.toIntOrNull()
                                if (manualVal != null && manualVal > 0) {
                                    manualVal
                                } else {
                                    val lockedItems = selectedTemplates.filter { (parsedCustomGrams[it.id] ?: 0) > 0 }
                                    val unlockedCount = (selectedTemplates.size - lockedItems.size).coerceAtLeast(1)
                                    val lockedCal = lockedItems.sumOf { l ->
                                        (l.calPer100g * ((parsedCustomGrams[l.id] ?: 0) / 100.0)).roundToInt()
                                    }
                                    val remCal = (currentFood.currentCal - lockedCal).coerceAtLeast(10)
                                    val shareCal = remCal.toDouble() / unlockedCount.toDouble()
                                    FoodItem.smartRoundGrams((shareCal / tpl.calPer100g.toDouble()) * 100.0)
                                }
                            } else 0

                            Surface(
                                color = if (isChecked) EmeraldLight else Color.White,
                                shape = RoundedCornerShape(10.dp),
                                border = BorderStroke(
                                    width = if (isChecked) 1.5.dp else 1.dp,
                                    color = if (isChecked) EmeraldPrimary else SlateCardBorder
                                ),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        if (isChecked) {
                                            selectedMixIds = selectedMixIds - tpl.id
                                        } else if (selectedMixIds.size < 10) {
                                            selectedMixIds = selectedMixIds + tpl.id
                                        }
                                    }
                            ) {
                                Row(
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Column(modifier = Modifier.weight(1f).padding(end = 6.dp)) {
                                        Text(
                                            text = if (isChecked) "☑ ${tpl.name}" else "☐ ${tpl.name}",
                                            fontSize = 12.sp,
                                            fontWeight = FontWeight.ExtraBold,
                                            color = if (isChecked) EmeraldDark else SlateTextDark
                                        )
                                        Text(
                                            text = if (isChecked) {
                                                "Dose nel Mix: ${autoAssignedGrams}g (~${((tpl.calPer100g * autoAssignedGrams) / 100.0).roundToInt()} kcal)"
                                            } else {
                                                "${tpl.calPer100g} kcal / 100g • ${tpl.category}"
                                            },
                                            fontSize = 10.sp,
                                            fontWeight = if (isChecked) FontWeight.Bold else FontWeight.Normal,
                                            color = if (isChecked) EmeraldPrimary else SlateTextMuted
                                        )
                                    }

                                    if (isChecked) {
                                        OutlinedTextField(
                                            value = manualText,
                                            onValueChange = { v ->
                                                val digits = v.filter { it.isDigit() }
                                                customMixGramsText = customMixGramsText.toMutableMap().apply {
                                                    put(tpl.id, digits)
                                                }
                                            },
                                            placeholder = { Text("${autoAssignedGrams}g", fontSize = 10.sp) },
                                            singleLine = true,
                                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                            modifier = Modifier
                                                .width(82.dp)
                                                .height(48.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }
                } else {
                    val rawOptions = if (selectedTab == 0 && officialGroup != null) officialGroup.options else smartOptions
                    val isSmartTab = selectedTab == 1 || officialGroup == null

                    // Calcola per OGNI opzione (anche nel tab Base!) la grammatura esatta per le calorie dello slot!
                    val exactDisplayedOptions = remember(rawOptions, currentFood.currentCal) {
                        rawOptions.map { opt -> currentFood.previewOptionForCurrentSlot(opt) }
                    }

                    LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.weight(1f, fill = false)) {
                        items(exactDisplayedOptions) { previewOpt ->
                            Surface(
                                color = Color.White,
                                shape = RoundedCornerShape(12.dp),
                                border = BorderStroke(1.dp, SlateCardBorder),
                                modifier = Modifier.fillMaxWidth().clickable { onSelectOption(previewOpt) }
                            ) {
                                Row(modifier = Modifier.padding(12.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                    Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                                        Text(
                                            text = "${previewOpt.category.uppercase()} (${previewOpt.baseGrams}G)",
                                            fontSize = 9.sp,
                                            fontWeight = FontWeight.ExtraBold,
                                            color = if (isSmartTab) PurpleColor else EmeraldDark
                                        )
                                        Text(previewOpt.name, fontSize = 13.sp, fontWeight = FontWeight.Bold, color = SlateTextDark)
                                        Text(
                                            text = "Dose esatta: ${previewOpt.baseGrams}g • ${previewOpt.baseCal} kcal (C:${previewOpt.baseCarb}g P:${previewOpt.baseProt}g G:${previewOpt.baseFat}g)",
                                            fontSize = 11.sp,
                                            fontWeight = FontWeight.SemiBold,
                                            color = SlateTextMuted
                                        )
                                    }
                                    Surface(color = if (isSmartTab) PurpleColor else EmeraldPrimary, shape = RoundedCornerShape(8.dp)) {
                                        Text("Usa", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold, maxLines = 1, modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp))
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RecipesDialog(highlightRecipeId: String?, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(18.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            modifier = Modifier.fillMaxWidth().heightIn(max = 580.dp)
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text("📖 Ricette & Ingredienti Word", fontSize = 15.sp, fontWeight = FontWeight.ExtraBold)
                    TextButton(onClick = onDismiss) { Text("✕", fontWeight = FontWeight.Bold, fontSize = 16.sp) }
                }
                HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                LazyColumn(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    items(DietRepository.recipes) { recipe ->
                        val isHi = recipe.id == highlightRecipeId
                        Surface(
                            color = if (isHi) EmeraldLight else Color.White,
                            shape = RoundedCornerShape(12.dp),
                            border = BorderStroke(if (isHi) 2.dp else 1.dp, if (isHi) EmeraldPrimary else SlateCardBorder),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                Text(recipe.dayReference.uppercase(), fontSize = 10.sp, fontWeight = FontWeight.ExtraBold, color = EmeraldDark)
                                Text(recipe.title, fontSize = 14.sp, fontWeight = FontWeight.ExtraBold, color = SlateTextDark)
                                Spacer(modifier = Modifier.height(4.dp))
                                Text("Ingredienti: ${recipe.ingredients}", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = SlateTextDark)
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(recipe.preparation, fontSize = 11.sp, color = SlateTextMuted)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SummaryMetricTile(
    title: String,
    value: String,
    unit: String,
    subtitle: String,
    color: Color,
    modifier: Modifier = Modifier
) {
    Surface(
        color = SlateBg,
        shape = RoundedCornerShape(10.dp),
        border = BorderStroke(1.dp, SlateCardBorder),
        modifier = modifier
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.padding(vertical = 8.dp, horizontal = 4.dp)
        ) {
            Text(title, fontSize = 9.sp, fontWeight = FontWeight.ExtraBold, color = SlateTextMuted, maxLines = 1)
            Spacer(modifier = Modifier.height(2.dp))
            Row(verticalAlignment = Alignment.Bottom) {
                Text(value, fontSize = 18.sp, fontWeight = FontWeight.Black, color = color)
                Spacer(modifier = Modifier.width(2.dp))
                Text(unit, fontSize = 9.sp, color = SlateTextMuted, fontWeight = FontWeight.Bold, modifier = Modifier.padding(bottom = 2.dp))
            }
            Text(subtitle, fontSize = 9.sp, color = SlateTextMuted, maxLines = 1)
        }
    }
}